import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Clock, AlertCircle, CheckCircle, XCircle, RefreshCw, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickTradeStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    fetchStatus();
    // Update every 1 minute
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => prev <= 1 ? 60 : prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [lastUpdate]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [confidenceRes, signalsRes, tickerRes, botStatusRes] = await Promise.all([
        axios.get(`${API}/market/confidence`),
        axios.get(`${API}/signals/external`),
        axios.get(`${API}/market/ticker`),
        axios.get(`${API}/bot/status`)
      ]);

      const confidence = confidenceRes.data;
      const signals = signalsRes.data;
      const ticker = tickerRes.data;
      const botStatus = botStatusRes.data;

      // Check if there's an open position
      const hasOpenPosition = botStatus?.bot_status?.open_position !== null;
      const openPosition = botStatus?.bot_status?.open_position;

      // Determine trade recommendation
      let recommendation = 'WAIT';
      let reason = '';
      let color = 'red';

      const score = confidence.score || 0;
      const threshold = confidence.threshold || 75;
      const canTrade = confidence.can_enter;
      const signalsAligned = signals.strategy_match;
      const consensus = signals.consensus?.signal;
      const rsi = confidence.indicators?.eth_1h_rsi || 50;

      // EXIT CONDITIONS (check first if in position)
      if (hasOpenPosition) {
        const entryPrice = openPosition?.entry_price || 0;
        const currentPrice = ticker.price || ticker.last;
        const pnlPercent = entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

        // Exit if: RSI overbought, consensus SHORT, or big loss
        if (rsi > 70) {
          recommendation = 'EXIT';
          reason = `RSI overbought (${rsi.toFixed(1)}) - Take profits now!`;
          color = 'orange';
        } else if (consensus === 'SHORT' && !signalsAligned) {
          recommendation = 'EXIT';
          reason = `Signals turned bearish - External consensus: SHORT`;
          color = 'orange';
        } else if (pnlPercent <= -0.8) {
          recommendation = 'EXIT';
          reason = `Near stop loss (${pnlPercent.toFixed(2)}%) - Consider manual exit`;
          color = 'orange';
        } else if (pnlPercent >= 1.5) {
          recommendation = 'EXIT';
          reason = `TP1 reached (+${pnlPercent.toFixed(2)}%) - Secure profits!`;
          color = 'orange';
        } else if (score < 40 && pnlPercent > 0) {
          recommendation = 'EXIT';
          reason = `Conditions weakening (score ${score}) - Lock in +${pnlPercent.toFixed(2)}% profit`;
          color = 'orange';
        } else {
          recommendation = 'HOLD';
          reason = `In position: ${pnlPercent >= 0 ? '+' : ''}${pnlPercent.toFixed(2)}% | Score: ${score}`;
          color = 'yellow';
        }
      } 
      // BUY CONDITIONS
      else if (score >= threshold && canTrade && signalsAligned && consensus === 'LONG') {
        recommendation = 'BUY NOW';
        reason = `Score ${score}/100 ✓ | Signals Aligned ✓ | Consensus LONG ✓`;
        color = 'green';
      } else if (score >= threshold - 10 && signalsAligned) {
        recommendation = 'HOLD';
        reason = `Score ${score}/100 (need ${threshold}) | Signals aligned, wait for better entry`;
        color = 'yellow';
      } else if (score >= threshold && !signalsAligned) {
        recommendation = 'HOLD';
        reason = `Score OK but external signals divergent - risky entry`;
        color = 'yellow';
      } else {
        recommendation = 'WAIT';
        const missing = [];
        if (score < threshold) missing.push(`Score ${score} < ${threshold}`);
        if (!signalsAligned) missing.push('Signals not aligned');
        if (confidence.no_trade_reasons?.length > 0) {
          missing.push(...confidence.no_trade_reasons);
        }
        reason = missing.join(' | ');
        color = 'red';
      }

      setStatus({
        recommendation,
        reason,
        color,
        score,
        threshold,
        price: ticker.price || ticker.last,
        consensus,
        signalsAligned,
        breakdown: confidence.breakdown,
        hasOpenPosition,
        openPosition,
        rsi,
        indicators: {
          rsi: confidence.indicators?.eth_1h_rsi,
          ema50: confidence.indicators?.eth_1h_ema50,
        }
      });

      setLastUpdate(new Date());
      setCountdown(60);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
    setLoading(false);
  };

  const getBackgroundClass = () => {
    if (!status) return 'bg-gray-800';
    switch (status.color) {
      case 'green': return 'bg-gradient-to-br from-green-900/80 to-green-800/50 border-green-500';
      case 'yellow': return 'bg-gradient-to-br from-yellow-900/80 to-yellow-800/50 border-yellow-500';
      case 'orange': return 'bg-gradient-to-br from-orange-900/80 to-orange-800/50 border-orange-500';
      case 'red': return 'bg-gradient-to-br from-red-900/80 to-gray-900/50 border-red-500';
      default: return 'bg-gray-800 border-gray-600';
    }
  };

  const getTextClass = () => {
    if (!status) return 'text-gray-400';
    switch (status.color) {
      case 'green': return 'text-green-400';
      case 'yellow': return 'text-yellow-400';
      case 'orange': return 'text-orange-400';
      case 'red': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getIcon = () => {
    if (!status) return <Clock className="text-gray-400" size={48} />;
    switch (status.recommendation) {
      case 'BUY NOW': return <CheckCircle className="text-green-400" size={48} />;
      case 'HOLD': return <AlertCircle className="text-yellow-400" size={48} />;
      case 'EXIT': return <LogOut className="text-orange-400" size={48} />;
      case 'WAIT': return <XCircle className="text-red-400" size={48} />;
      default: return <Clock className="text-gray-400" size={48} />;
    }
  };

  return (
    <div className={`rounded-xl p-6 border-2 ${getBackgroundClass()} transition-all duration-500`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <TrendingUp size={24} />
          Quick Trade Status
        </h2>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <Clock size={14} />
            Next update: {countdown}s
          </div>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
            title="Refresh now"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Main Recommendation */}
      <div className="flex items-center gap-6 mb-6">
        {getIcon()}
        <div>
          <div className={`text-4xl font-black ${getTextClass()}`}>
            {status?.recommendation || 'Loading...'}
          </div>
          <div className="text-sm text-gray-400 mt-1 max-w-md">
            {status?.reason || 'Analyzing market conditions...'}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Price */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">ETH Price</div>
            <div className="text-lg font-bold text-blue-400">
              ${status.price?.toFixed(2) || '---'}
            </div>
          </div>

          {/* Confidence Score */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Confidence</div>
            <div className={`text-lg font-bold ${status.score >= status.threshold ? 'text-green-400' : 'text-red-400'}`}>
              {status.score}/{status.threshold}
            </div>
          </div>

          {/* RSI */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">RSI (14)</div>
            <div className={`text-lg font-bold ${
              status.rsi > 70 ? 'text-red-400' : 
              status.rsi < 30 ? 'text-green-400' : 'text-yellow-400'
            }`}>
              {status.rsi?.toFixed(1) || '---'}
            </div>
          </div>

          {/* Signals */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">External Signals</div>
            <div className={`text-lg font-bold ${status.signalsAligned ? 'text-green-400' : 'text-yellow-400'}`}>
              {status.signalsAligned ? '✓ Aligned' : '⚠ Divergent'}
            </div>
          </div>

          {/* Consensus */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Consensus</div>
            <div className={`text-lg font-bold ${
              status.consensus === 'LONG' ? 'text-green-400' : 
              status.consensus === 'SHORT' ? 'text-red-400' : 'text-gray-400'
            }`}>
              {status.consensus || 'NEUTRAL'}
            </div>
          </div>
        </div>
      )}

      {/* Open Position Info */}
      {status?.hasOpenPosition && status?.openPosition && (
        <div className="mt-4 p-4 bg-purple-900/30 border border-purple-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-purple-400 mb-1">📊 Open Position</div>
              <div className="text-lg font-bold text-white">
                LONG @ ${status.openPosition.entry_price?.toFixed(2)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400 mb-1">Unrealized P/L</div>
              {(() => {
                const pnl = status.openPosition.entry_price > 0 
                  ? ((status.price - status.openPosition.entry_price) / status.openPosition.entry_price) * 100 
                  : 0;
                return (
                  <div className={`text-xl font-bold ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Score Breakdown Mini */}
      {status?.breakdown && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="text-xs text-gray-500 mb-2">Score Breakdown</div>
          <div className="flex gap-2 flex-wrap">
            <span className={`px-2 py-1 rounded text-xs ${status.breakdown.filter_4h >= 20 ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
              4H: {status.breakdown.filter_4h}/30
            </span>
            <span className={`px-2 py-1 rounded text-xs ${status.breakdown.trend_1h >= 20 ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
              1H: {status.breakdown.trend_1h}/30
            </span>
            <span className={`px-2 py-1 rounded text-xs ${status.breakdown.timing >= 15 ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
              Timing: {status.breakdown.timing}/25
            </span>
            <span className={`px-2 py-1 rounded text-xs ${status.breakdown.risk >= 10 ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
              Risk: {status.breakdown.risk}/15
            </span>
          </div>
        </div>
      )}

      {/* Last Update */}
      <div className="mt-4 text-xs text-gray-500 text-right">
        Last updated: {lastUpdate?.toLocaleTimeString() || 'Never'}
      </div>
    </div>
  );
};

export default QuickTradeStatus;
